import React, { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaMinus, FaPlus } from 'react-icons/fa';

import ModalStatus from '@/components/ModalStatus';
import { useAlertDialog } from '@/components/ui/alert-dialog';
import Breadcrumb from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { FormGrid } from '@/components/ui/form-grid';
import { FormItem } from '@/components/ui/form-item';
import { Input } from '@/components/ui/input';
import { PageTitle } from '@/components/ui/page-title';
import { Can } from '@/hooks/ability';
import PermissionData from '@/types/Permission/PermissionData';
import PermissionFormData from '@/types/Permission/PermissionFormData';

import { useStorePermissionMutation } from './api/use-store-permission.mutation';
import { useUpdatePermissionMutation } from './api/use-update-permission.mutation';
import { PermissionsTable } from './permissions.table';

const initialValues: PermissionFormData = {
  name: '',
  description: '',
};

export const Permissions = () => {
  const { t } = useTranslation();
  const form = useForm({
    defaultValues: initialValues,
  });
  const { alert } = useAlertDialog();

  const [visibilityForm, setVisibilityForm] = useState(false);
  const [editing, setEditing] = useState(false);

  const tableRefetchRef = useRef<(() => void) | null>(null);

  const storePermissionMutation = useStorePermissionMutation();
  const updatePermissionMutation = useUpdatePermissionMutation();

  const handleSuccess = (messageSuccess = '') =>
    ModalStatus({
      type: 'success',
      title: t('modal.success'),
      subTitle: messageSuccess,
    });

  const handleHideForm = useCallback(() => {
    setVisibilityForm(false);
    setEditing(false);
    form.reset(initialValues);
  }, [form]);

  const handleDisplayForm = useCallback(() => {
    const willOpen = !visibilityForm;
    setVisibilityForm(willOpen);
    if (willOpen) {
      setEditing(false);
      form.reset(initialValues);
    }
  }, [visibilityForm, form]);

  const handleEdit = useCallback(
    (record: PermissionData) => {
      form.reset({
        id: record.id,
        name: record.name,
        description: record.description,
      });
      setVisibilityForm(true);
      setEditing(true);
    },
    [form],
  );

  const handleRegister = useCallback(
    async (values: PermissionFormData) => {
      try {
        await storePermissionMutation.mutateAsync(values, {
          onSuccess: () => {
            handleSuccess(t('permission-registered-with-success'));
            handleHideForm();
            tableRefetchRef.current?.();
          },
        });
      } catch {
        alert({
          type: 'error',
          title: t('error'),
        });
      }
    },
    [storePermissionMutation, handleHideForm, t],
  );

  const handleUpdate = useCallback(
    async (values: PermissionFormData) => {
      try {
        await updatePermissionMutation.mutateAsync(values, {
          onSuccess: () => {
            handleSuccess(t('permission-updated-with-success'));
            handleHideForm();
            tableRefetchRef.current?.();
          },
        });
      } catch {
        alert({
          type: 'error',
          title: t('error'),
        });
      }
    },
    [updatePermissionMutation, handleHideForm, t],
  );

  const onSubmit = async (values: PermissionFormData) => {
    if (editing) {
      await handleUpdate(values);
    } else {
      await handleRegister(values);
    }
  };

  const setTableRefetch = useCallback((refetch: () => void) => {
    tableRefetchRef.current = refetch;
  }, []);

  return (
    <div className="flex h-full w-full flex-col overflow-y-hidden">
      <Breadcrumb
        items={[{ title: t('breadcrumb.admin') }, { title: t('breadcrumb.permissions'), to: '/admin/permission' }]}
      />

      <div className="flex h-fit max-h-full flex-col gap-2 overflow-y-hidden p-4">
        <div className="flex justify-between">
          <PageTitle>{t('pages.admin.permission.title_page')}</PageTitle>

          <Can I="admin.permission.store" a="">
            <Button onClick={handleDisplayForm}>
              {visibilityForm ? <FaMinus /> : <FaPlus />}
              {t('pages.admin.permission.buttonAdd')}
            </Button>
          </Can>
        </div>

        {visibilityForm && (
          <div className="rounded-md border border-gray-200 bg-white p-4">
            <h2 className="mb-4 text-base font-semibold text-gray-700">{t('pages.admin.permission.registerTitle')}</h2>
            <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
              <FormGrid>
                <FormItem
                  name="name"
                  label={t('pages.admin.permission.form.name')}
                  rules={{
                    required: true,
                    minLength: 3,
                    maxLength: 255,
                  }}
                >
                  <Input maxLength={255} />
                </FormItem>
              </FormGrid>

              <FormGrid>
                <FormItem
                  name="description"
                  label={t('pages.admin.permission.form.description')}
                  rules={{
                    required: true,
                    minLength: 3,
                    maxLength: 255,
                  }}
                >
                  <Input maxLength={255} />
                </FormItem>
              </FormGrid>

              <div className="mt-6 flex w-full justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleHideForm}
                  isLoading={storePermissionMutation.isPending || updatePermissionMutation.isPending}
                >
                  {t('pages.admin.permission.buttonCancel')}
                </Button>
                <Button
                  type="submit"
                  isLoading={storePermissionMutation.isPending || updatePermissionMutation.isPending}
                >
                  {editing ? t('pages.admin.permission.buttonEdit') : t('pages.admin.permission.buttonRegister')}
                </Button>
              </div>
            </Form>
          </div>
        )}

        <PermissionsTable onEdit={handleEdit} setTableRefetch={setTableRefetch} />
      </div>
    </div>
  );
};
